import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { showLoader, hideLoader } from "./LoaderSlice";
import {
  addProduct,
  deleteProduct,
  editProduct,
  getProduct,
  getProductDetails,
  getProductList,
} from "../../services";
import { showMessage } from "../../helpers";

// Create async thunks for API calls
export const adminAddProduct = createAsyncThunk(
  "adminProducts/addProduct",
  async ({ data, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      await addProduct(data);
      dispatch(hideLoader());
      showMessage("success", "Product added successfully!");
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (error) {
      dispatch(hideLoader());
      showMessage("error", error.toString());
      throw error;
    }
  },
);

export const adminEditProduct = createAsyncThunk(
  "adminProducts/editProduct",
  async ({ id, data, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      await editProduct(id, data);
      dispatch(hideLoader());
      showMessage("success", "Product edited successfully!");
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (error) {
      dispatch(hideLoader());
      showMessage("error", error.toString());
      throw error;
    }
  },
);

export const getAdminProductList = createAsyncThunk(
  "adminProducts/getProductList",
  async (showRecent, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getProductList(showRecent);
      dispatch(hideLoader());
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const getAdminProduct = createAsyncThunk(
  "adminProducts/getProduct",
  async ({ id, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getProduct(id);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess();
      }
      return response || {};
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const deleteAdminProduct = createAsyncThunk(
  "adminProducts/deleteProduct",
  async ({ id, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      await deleteProduct(id);
      dispatch(hideLoader());
      showMessage("success", "Product Deactivated!");
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const getAdminProductDetails = createAsyncThunk(
  "adminProducts/getProductDetails",
  async ({ data, onSuccess }, { dispatch }) => {
    try {
      const response = await getProductDetails(data);
      if (onSuccess) {
        onSuccess(response || {});
      }
      return response || {};
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
);

const initialState = {
  isProductAdded: false,
  isProductEdited: false,
  isProductLoaded: false,
  productList: [],
  productItem: {},
  productDetails: {},
  isLoaded: false,
};

const adminProductsSlice = createSlice({
  name: "adminProducts",
  initialState,
  reducers: {
    resetProduct: (state) => {
      state.isProductAdded = false;
      state.isProductEdited = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add Product
      .addCase(adminAddProduct.pending, (state) => {
        state.isProductAdded = false;
      })
      .addCase(adminAddProduct.fulfilled, (state) => {
        state.isProductAdded = true;
      })
      .addCase(adminAddProduct.rejected, (state) => {
        state.isProductAdded = false;
      })

      // Edit Product
      .addCase(adminEditProduct.pending, (state) => {
        state.isProductEdited = false;
      })
      .addCase(adminEditProduct.fulfilled, (state) => {
        state.isProductEdited = true;
      })
      .addCase(adminEditProduct.rejected, (state) => {
        state.isProductEdited = false;
      })

      // Get Product List
      .addCase(getAdminProductList.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(getAdminProductList.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.productList = action.payload;
      })
      .addCase(getAdminProductList.rejected, (state) => {
        state.isLoaded = true;
      })

      // Get Product
      .addCase(getAdminProduct.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(getAdminProduct.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.productItem = action.payload;
      })
      .addCase(getAdminProduct.rejected, (state) => {
        state.isLoaded = true;
      })

      // Get Product Details
      .addCase(getAdminProductDetails.pending, (state) => {
        state.isProductLoaded = false;
      })
      .addCase(getAdminProductDetails.fulfilled, (state, action) => {
        state.isProductLoaded = true;
        state.productDetails = action.payload;
      })
      .addCase(getAdminProductDetails.rejected, (state) => {
        state.isProductLoaded = true;
      });
  },
});

export const { resetProduct } = adminProductsSlice.actions;
export default adminProductsSlice.reducer;
